import { useEffect, useState } from 'react'
import { AlertTriangle, ArrowRightLeft } from 'lucide-react'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { useGetManagerOptions, useSetDriverManager } from '../../hooks/backend/drivers'
import { runConfirmed } from '../../hooks/confirmedWrites'
import { writeErrorMessage } from '../../utils/writeError'
import type { ManagerOption } from '../../utils/managers'

// One control for a driver's manager, used everywhere a driver appears: Active
// Drivers, Vehicle Allocation, the driver detail drawer, Driver Pipeline and
// Contract Rollout. One behaviour to learn and one thing to fix.
//
// The driver's own row is the record of truth. Where the driver's manager
// disagrees with the manager of the car they are seated in, this shows an amber
// badge and offers a one-tap "Match to car" rather than resolving it silently.
//
// On Vehicle Allocation this control sits on the SEAT. The control that moves
// the car itself is a separate, differently labelled one on the car header, so
// an operator cannot move a car when they meant to move a person.

export type ManagerState = 'ok' | 'mismatch' | 'unassigned' | 'car_unassigned' | 'not_seated'

export function ManagerCell({
  driverId,
  driverName,
  managerPersonId,
  managerState,
  plate,
  vehicleManagerPersonId,
  vehicleManagerName,
  canEdit,
  requireReason,
  onChanged,
  className,
}: {
  driverId: string
  driverName: string
  managerPersonId: string | null
  managerState: ManagerState
  plate?: string | null
  vehicleManagerPersonId?: string | null
  vehicleManagerName?: string | null
  canEdit: boolean
  // A seated driver's move must carry a reason. A benched driver being given a
  // manager for the first time does not need one.
  requireReason?: boolean
  onChanged: () => void
  className?: string
}) {
  const options = useGetManagerOptions()
  const setManager = useSetDriverManager()
  const [busy, setBusy] = useState(false)
  // What the operator chose, held for exactly as long as the request is in
  // flight, so a controlled select never paints the new name and then snaps
  // back to the old one while it saves.
  const [pending, setPending] = useState<string | null>(null)

  // A page can render thirty of these. useBackend de-duplicates in-flight calls
  // and caches for 15 seconds on the same route and params, so this is one
  // request for the whole grid, not one per seat. skipCache is deliberately NOT
  // passed here: the list of Driver Servants changes about once a year.
  const optionsTrigger = options.trigger
  useEffect(() => {
    void optionsTrigger({})
  }, [optionsTrigger])

  const list = (options.data as ManagerOption[] | undefined) ?? []

  async function apply(nextManagerId: string, reason: string | null) {
    if (!nextManagerId || nextManagerId === managerPersonId) return
    setBusy(true)
    setPending(nextManagerId)
    try {
      // Shown once the server has answered, held until the board has been
      // re-read. `manager_state` moves with the manager because the amber
      // mismatch badge is derived from it, and a badge still saying mismatch
      // after the driver has been matched to the car is the same confusion as a
      // stale name, only louder.
      const nextManagerName =
        list.find((o) => o.manager_person_id === nextManagerId)?.manager_name ?? null
      const nextState: ManagerState = !vehicleManagerPersonId
        ? 'car_unassigned'
        : nextManagerId === vehicleManagerPersonId
          ? 'ok'
          : 'mismatch'
      const result = (await runConfirmed(
        () =>
          setManager.trigger({
            driver_id: driverId,
            manager_person_id: nextManagerId,
            reason,
          }).result,
        () => ({
          label: 'driver manager',
          routes: ['drivers/getDriverManagerBoard'],
          match: (row: Record<string, unknown>) => row.driver_id === driverId,
          set: {
            manager_person_id: nextManagerId,
            manager_name: nextManagerName,
            manager_state: nextState,
          },
        }),
      )) as { message?: string } | null
      toast.success(result?.message ?? `${driverName} moved.`)
      onChanged()
    } catch (err) {
      toast.error('Could not change this driver’s manager', {
        description: writeErrorMessage(err),
      })
    } finally {
      setBusy(false)
      setPending(null)
    }
  }

  function handleSelect(nextManagerId: string) {
    if (!nextManagerId || nextManagerId === managerPersonId) return
    let reason: string | null = null
    if (requireReason) {
      // Deliberately a prompt rather than a silent write. Three seconds of
      // typing is the difference between an audit trail and a list of
      // timestamps, and a driver can always be told why their manager changed.
      const entered = window.prompt(
        `Why is ${driverName} moving to a different manager?`,
        '',
      )
      if (entered === null) return
      reason = entered.trim() || null
      if (!reason) {
        toast.error('A reason is required when moving a driver who is on a car.')
        return
      }
    }
    void apply(nextManagerId, reason)
  }

  function handleMatchToCar() {
    if (!vehicleManagerPersonId) return
    void apply(
      vehicleManagerPersonId,
      `Matched to the manager of ${plate ?? 'the seated car'}.`,
    )
  }

  const mismatched = managerState === 'mismatch'
  const unassigned = managerState === 'unassigned'

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-center gap-2">
        <NativeSelect
          aria-label={`Manager for ${driverName}`}
          value={pending ?? managerPersonId ?? ''}
          disabled={!canEdit || busy || options.loading}
          title={busy ? 'Saving. The board updates once the database confirms it.' : undefined}
          onChange={(e) => handleSelect(e.target.value)}
          className={cn(
            'h-8 text-xs',
            unassigned && 'border-destructive text-destructive',
            mismatched && 'border-amber-500',
          )}
        >
          <option value="">No manager</option>
          {list.map((o) => (
            <option key={o.manager_person_id} value={o.manager_person_id}>
              {o.manager_name}
            </option>
          ))}
        </NativeSelect>
        {busy && <Spinner className="h-3.5 w-3.5" />}
      </div>

      {mismatched && (
        <div className="flex items-center gap-1.5 text-[11px] text-amber-600">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          <span>
            {plate} is on {vehicleManagerName}’s book
          </span>
          {canEdit && (
            <button
              type="button"
              onClick={handleMatchToCar}
              disabled={busy}
              className="inline-flex items-center gap-1 font-medium underline underline-offset-2 hover:no-underline"
            >
              <ArrowRightLeft className="h-3 w-3" />
              Match to car
            </button>
          )}
        </div>
      )}

      {managerState === 'car_unassigned' && (
        <span className="text-[11px] text-muted-foreground">
          {plate} has no manager yet
        </span>
      )}
    </div>
  )
}
